<template>
  <div v-if="loading" class="text-center py-16">
    <v-progress-circular indeterminate color="#f43f5e" size="56" width="4" />
    <div class="text-body-2 text-medium-emphasis mt-4">Loading audiobook details...</div>
  </div>

  <v-alert v-else-if="error" type="error" class="my-6" variant="tonal">{{ error }}</v-alert>

  <div v-else-if="audiobook" class="audiobook-detail-view">
    <!-- Atmospheric Backdrop Hero -->
    <div class="hero-backdrop-container">
      <div
        class="hero-backdrop-image"
        :style="{ backgroundImage: `url(${posterSrc(audiobook)})` }"
      ></div>
      <div class="hero-backdrop-overlay"></div>
    </div>

    <!-- Navigation Bar -->
    <div class="d-flex align-center justify-space-between mb-6 position-relative z-index-2">
      <v-btn
        to="/audiobooks/homeview"
        variant="tonal"
        size="small"
        class="btn-modern"
        prepend-icon="mdi-arrow-left"
      >
        Back to Audiobooks
      </v-btn>
    </div>

    <!-- Hero Content Card -->
    <div class="hero-content-card glass-card pa-6 pa-md-8 mb-8 position-relative z-index-2">
      <v-row align="start">
        <!-- Poster Column -->
        <v-col cols="12" sm="5" md="4" lg="3" class="d-flex justify-center justify-sm-start">
          <div class="poster-3d-wrapper">
            <v-img
              :src="posterSrc(audiobook)"
              :aspect-ratio="2 / 3"
              cover
              class="poster-3d-image rounded-xl"
            >
              <template #placeholder>
                <div class="poster-3d-placeholder">
                  <v-icon size="64" color="#f43f5e">mdi-headphones</v-icon>
                </div>
              </template>
            </v-img>
            <div v-if="audiobook.duration" class="poster-duration-badge">
              {{ audiobook.duration }}
            </div>
          </div>
        </v-col>

        <!-- Metadata & Actions Column -->
        <v-col cols="12" sm="7" md="8" lg="9">
          <div class="d-flex flex-column h-100 justify-space-between">
            <div>
              <!-- Genre & Status Badges -->
              <div class="d-flex flex-wrap align-center ga-2 mb-3">
                <v-chip v-if="audiobook.category" color="#f43f5e" variant="flat" size="small" class="text-white font-weight-bold">
                  {{ audiobook.category }}
                </v-chip>
                <v-chip v-if="audiobook.year" size="small" variant="tonal" class="font-weight-medium">
                  {{ audiobook.year }}
                </v-chip>
                <v-chip v-if="audiobook.duration" size="small" variant="tonal" class="font-weight-medium">
                  <v-icon start size="14">mdi-clock-outline</v-icon>
                  {{ audiobook.duration }}
                </v-chip>
                <v-chip v-if="audiobook.language" size="small" variant="tonal" class="font-weight-medium">
                  <v-icon start size="14">mdi-translate</v-icon>
                  {{ audiobook.language }}
                </v-chip>
              </div>

              <!-- Titles, Author & Narrator -->
              <h1 class="audiobook-detail-title mb-1">{{ audiobook.title }}</h1>
              <div v-if="audiobook.author" class="text-subtitle-1 text-medium-emphasis mb-1">
                Written by <span class="text-high-emphasis font-weight-bold">{{ audiobook.author }}</span>
              </div>
              <div v-if="audiobook.narrator" class="text-subtitle-2 text-medium-emphasis mb-2">
                Narrated by <span class="text-high-emphasis font-weight-medium">{{ audiobook.narrator }}</span>
              </div>
              <p v-if="audiobook.original && audiobook.original !== audiobook.title" class="text-caption text-medium-emphasis mb-4">
                <em>Original: {{ audiobook.original }}</em>
              </p>

              <!-- Rating Score -->
              <div v-if="audiobook.rating && Number(audiobook.rating) > 0" class="rating-display-box d-flex align-center ga-3 mb-6">
                <v-rating
                  :model-value="Number(audiobook.rating)"
                  half-increments
                  readonly
                  density="compact"
                  color="amber"
                  active-color="amber"
                />
                <span class="text-subtitle-1 font-weight-bold">{{ audiobook.rating }} / 5</span>
              </div>
            </div>

            <!-- Action Toolbar -->
            <div class="action-toolbar d-flex flex-wrap align-center ga-2 pt-4 border-t-subtle">
              <!-- Wishlist Toggle -->
              <v-btn
                v-if="isWishList"
                color="#f43f5e"
                variant="flat"
                class="btn-modern text-white font-weight-bold"
                prepend-icon="mdi-check-circle"
                :loading="movingToCollection"
                @click="moveToCollection"
              >
                Move to Owned
              </v-btn>

              <!-- Favorite Toggle -->
              <v-btn
                variant="tonal"
                :color="isFavorite ? 'pink' : undefined"
                class="btn-modern"
                :prepend-icon="isFavorite ? 'mdi-heart' : 'mdi-heart-outline'"
                :loading="togglingFavorite"
                @click="toggleFavorite"
              >
                {{ isFavorite ? 'Favorited' : 'Favorite' }}
              </v-btn>

              <!-- Edit Post -->
              <v-btn
                :to="{ name: 'EditAudiobookPost', params: { id: audiobook.id } }"
                variant="tonal"
                class="btn-modern"
                prepend-icon="mdi-pencil-outline"
              >
                Edit
              </v-btn>

              <v-spacer></v-spacer>

              <!-- Delete Action -->
              <v-btn
                color="error"
                variant="tonal"
                class="btn-modern"
                prepend-icon="mdi-delete-outline"
                :loading="deleting"
                @click="remove"
              >
                Delete
              </v-btn>
            </div>
          </div>
        </v-col>
      </v-row>
    </div>

    <!-- Technical Specs Quick Grid -->
    <v-row class="mb-6" dense>
      <v-col v-if="audiobook.author" cols="6" sm="4" md="2">
        <v-card class="glass-card pa-4 text-center h-100">
          <div class="spec-label">Author</div>
          <div class="spec-value text-truncate">{{ audiobook.author }}</div>
        </v-card>
      </v-col>
      <v-col v-if="audiobook.narrator" cols="6" sm="4" md="2">
        <v-card class="glass-card pa-4 text-center h-100">
          <div class="spec-label">Narrator</div>
          <div class="spec-value text-truncate">{{ audiobook.narrator }}</div>
        </v-card>
      </v-col>
      <v-col v-if="audiobook.duration" cols="6" sm="4" md="2">
        <v-card class="glass-card pa-4 text-center h-100">
          <div class="spec-label">Runtime</div>
          <div class="spec-value">{{ audiobook.duration }}</div>
        </v-card>
      </v-col>
      <v-col v-if="audiobook.year || audiobook.publishedDate" cols="6" sm="4" md="2">
        <v-card class="glass-card pa-4 text-center h-100">
          <div class="spec-label">Published</div>
          <div class="spec-value">{{ audiobook.publishedDate || audiobook.year }}</div>
        </v-card>
      </v-col>
      <v-col v-if="audiobook.language" cols="6" sm="4" md="2">
        <v-card class="glass-card pa-4 text-center h-100">
          <div class="spec-label">Language</div>
          <div class="spec-value">{{ audiobook.language }}</div>
        </v-card>
      </v-col>
      <v-col v-if="audiobook.created" cols="6" sm="4" md="2">
        <v-card class="glass-card pa-4 text-center h-100">
          <div class="spec-label">Date Added</div>
          <div class="spec-value">{{ formatDate(audiobook.created) }}</div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Narrative Section -->
    <Synopsis :audiobook="audiobook" />
  </div>
</template>

<script>
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import Synopsis from '@/components/audiobooks/Synopsis.vue'
import AudiobooksAPI from '@/services/audiobooks'
import { posterSrc } from '@/utils/audiobookForm'

export default {
  name: 'AudiobookDetail',
  components: { Synopsis },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const audiobook = ref(null)
    const loading = ref(true)
    const error = ref('')
    const deleting = ref(false)
    const togglingFavorite = ref(false)
    const movingToCollection = ref(false)

    const postMarked = (value) => {
      if (value == null || value === '') return '0'
      return String(value)
    }

    const isWishList = computed(() => postMarked(audiobook.value?.marked) === '2')
    const isFavorite = computed(() => postMarked(audiobook.value?.marked) === '1')

    const load = async () => {
      loading.value = true
      error.value = ''
      try {
        const { data } = await AudiobooksAPI.getPostByID(route.params.id)
        audiobook.value = data
      } catch (err) {
        error.value = err.response?.data?.detail?.message || 'Audiobook not found'
      } finally {
        loading.value = false
      }
    }

    const remove = async () => {
      if (!confirm(`Delete "${audiobook.value?.title || 'this audiobook'}" from your library?`)) return
      deleting.value = true
      try {
        await AudiobooksAPI.deletePost(route.params.id)
        router.push({ name: 'AudiobooksHome', query: { message: 'Audiobook deleted.' } })
      } catch (err) {
        error.value = err.response?.data?.detail?.message || 'Delete failed'
      } finally {
        deleting.value = false
      }
    }

    const toggleFavorite = async () => {
      togglingFavorite.value = true
      try {
        const { data } = await AudiobooksAPI.toggleFavorite(route.params.id)
        audiobook.value = data
      } catch (err) {
        error.value = err.response?.data?.detail?.message || 'Failed to update favorite'
      } finally {
        togglingFavorite.value = false
      }
    }

    const moveToCollection = async () => {
      movingToCollection.value = true
      try {
        const { data } = await AudiobooksAPI.moveToCollection(route.params.id)
        audiobook.value = data
      } catch (err) {
        error.value = err.response?.data?.detail?.message || 'Failed to move to collection'
      } finally {
        movingToCollection.value = false
      }
    }

    const formatDate = (value) => {
      if (!value) return ''
      return new Date(value).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
    }

    onMounted(load)

    return {
      audiobook,
      loading,
      error,
      deleting,
      togglingFavorite,
      movingToCollection,
      isWishList,
      isFavorite,
      remove,
      toggleFavorite,
      moveToCollection,
      posterSrc,
      formatDate,
    }
  },
}
</script>

<style scoped>
.audiobook-detail-view {
  max-width: 1240px;
  margin: 0 auto;
  position: relative;
}

/* Atmospheric Backdrop */
.hero-backdrop-container {
  position: absolute;
  top: -40px;
  left: -200px;
  right: -200px;
  height: 480px;
  overflow: hidden;
  pointer-events: none;
  z-index: 0;
}

.hero-backdrop-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center 25%;
  filter: blur(48px);
  opacity: 0.35;
  transform: scale(1.15);
}

.hero-backdrop-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: radial-gradient(circle at center, transparent 0%, rgba(10, 14, 26, 0.95) 100%);
}

.z-index-2 {
  z-index: 2;
}

/* Poster 3D Presentation */
.poster-3d-wrapper {
  position: relative;
  width: 100%;
  max-width: 260px;
}

.poster-3d-image {
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.4), 0 0 20px rgba(244, 63, 94, 0.3);
}

.poster-3d-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 360px;
  background: linear-gradient(135deg, rgba(244, 63, 94, 0.15), rgba(251, 113, 133, 0.15));
}

.poster-duration-badge {
  position: absolute;
  bottom: 12px;
  right: 12px;
  background: rgba(244, 63, 94, 0.9);
  backdrop-filter: blur(8px);
  color: #fff;
  font-size: 0.75rem;
  font-weight: 800;
  padding: 4px 8px;
  border-radius: 6px;
}

/* Titles */
.audiobook-detail-title {
  font-size: clamp(1.75rem, 3.5vw, 2.75rem);
  font-weight: 800;
  line-height: 1.15;
  letter-spacing: -0.02em;
}

.border-t-subtle {
  border-top: 1px solid rgba(128, 128, 128, 0.12);
}

/* Spec Box */
.spec-label {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgba(128, 128, 128, 0.8);
  margin-bottom: 4px;
}

.spec-value {
  font-size: 0.95rem;
  font-weight: 600;
}
</style>

